import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent, CardHeader, Skeleton } from '@ibsys/kit';
import { FC, useEffect, useMemo, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useCreateSample, useDeleteSample, useSample, useUpdateSample } from '@/entities/sample';
import { useAppNavigate } from '@/shared/lib';
import { SampleForm } from './SampleForm';
import { SampleFormValues, useSampleSchema } from './useSampleSchema';
import { useFormGuardActions } from '@/entities/formGuard';

type Props = {
  sampleId?: number;
  isCreating: boolean;
};

export const SampleCard: FC<Props> = ({ sampleId, isCreating }) => {
  const { data, isLoading } = useSample(!isCreating ? sampleId : undefined);
  const navigate = useAppNavigate();
  const schema = useSampleSchema();
  const { setIsDirty } = useFormGuardActions();

  // Get conversationIds from API response
  const apiConversationIds = useMemo(() => data?.conversations?.map((c) => c.id) ?? [], [data?.conversations]);

  const defaults: SampleFormValues = useMemo(
    () => ({
      name: data?.name ?? '',
      description: data?.description ?? '',
      predicate: data?.predicate ?? { dsl: '', dslHuman: '' },
      conversationIds: apiConversationIds,
    }),
    [data, apiConversationIds],
  );

  const form = useForm<SampleFormValues>({
    defaultValues: defaults,
    resolver: zodResolver(schema),
  });
  const { formState } = form;

  useEffect(() => {
    setIsDirty(formState.isDirty);
  }, [formState.isDirty]);

  // Track the previous sampleId to detect when it changes
  const prevSampleIdRef = useRef<number | undefined>(sampleId);

  useEffect(() => {
    if (isCreating) {
      form.reset({ name: '', description: '', predicate: { dsl: '', dslHuman: '' }, conversationIds: [] });
    } else if (prevSampleIdRef.current !== sampleId) {
      // Only reset form when sampleId changes, not on every render
      form.reset(defaults);
      prevSampleIdRef.current = sampleId;
    } else {
      // For same sampleId, only update non-conversationIds fields
      const currentConversationIds = form.getValues('conversationIds');
      form.reset({
        ...defaults,
        conversationIds: currentConversationIds.length > 0 ? currentConversationIds : defaults.conversationIds,
      });
    }
  }, [form, defaults, isCreating, sampleId]);

  const { mutateAsync: deleteSample } = useDeleteSample();
  const { mutateAsync: createSample, isPending: isLoadingCreating } = useCreateSample();
  const { mutateAsync: updateSample, isPending: isLoadingUpdating } = useUpdateSample();

  const isLoadingSaving = isLoadingCreating || isLoadingUpdating;

  const handleSave = async (values: SampleFormValues) => {
    const predicate = values.predicate?.dsl ? values.predicate : undefined;

    if (isCreating) {
      const { id } = await createSample({
        name: values.name,
        description: values.description || undefined,
        predicate,
        conversationIds: values.conversationIds.length > 0 ? values.conversationIds : undefined,
      });
      // Отправленное становится сохранённым: карточка перестаёт считать себя изменённой
      form.reset(values);
      setIsDirty(false);
      navigate('/samples/:id', { id: id.toString() });
    } else if (sampleId) {
      await updateSample({
        id: sampleId,
        payload: {
          name: values.name,
          description: values.description || undefined,
          predicate,
          conversationIds: values.conversationIds.length > 0 ? values.conversationIds : undefined,
        },
      });
      form.reset(values);
    }
  };

  const handleUpdateName = async (name: string) => {
    if (!sampleId || !data) return;
    await updateSample({
      id: sampleId,
      payload: {
        name,
        description: data.description,
        predicate: data.predicate ?? undefined,
      },
    });
  };

  const handleDelete = async () => {
    if (sampleId) {
      await deleteSample(sampleId);
      setIsDirty(false);
      navigate('/samples');
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="w-45 h-8" />
        </CardHeader>
        <CardContent>
          <Skeleton className="w-full h-40" />
        </CardContent>
      </Card>
    );
  }

  return (
    <FormProvider {...form}>
      <SampleForm
        key={`${isCreating}-${sampleId}`}
        sampleId={sampleId}
        isCreating={isCreating}
        isLoadingSaving={isLoadingSaving}
        predicateDsl={data?.predicate?.dsl}
        onSave={handleSave}
        onDelete={handleDelete}
        onUpdateName={handleUpdateName}
      />
    </FormProvider>
  );
};
